/** Assessment methodology and client persistence settings. */

export const ASSESSMENT_METHODOLOGY_VERSION = '1.1.0'

export const ASSESSMENT_BASE_PATH = '/tools/uae-sustainability-assessment'

export const SESSION_STORAGE_KEY = 'ecopshub.uae-assessment.v1'

/** Session resume is enabled for v1 (documented in privacy page). */
export const SESSION_STORAGE_ENABLED = true

export const ASSESSMENT_STEPS = [
  { id: 'company', label: 'Company profile', estimateMin: 2 },
  { id: 'reporting', label: 'Reporting context', estimateMin: 2 },
  { id: 'materiality', label: 'What matters most', estimateMin: 3 },
  { id: 'carbon', label: 'Carbon readiness', estimateMin: 3 },
  { id: 'evidence', label: 'Evidence & governance', estimateMin: 2 },
  { id: 'stakeholders', label: 'Stakeholder expectations', estimateMin: 1 },
] as const

export type AssessmentStepId = (typeof ASSESSMENT_STEPS)[number]['id']

/** Plain-language rating scale shown to users (maps to financial / impact scores 1–5). */
export const RATING_SCALE = {
  1: {
    label: 'Almost none',
    guidance: 'Little to no effect on the business, people, or environment right now.',
  },
  2: {
    label: 'Small',
    guidance: 'Some effect, but usually manageable with normal day-to-day controls.',
  },
  3: {
    label: 'Noticeable',
    guidance: 'Clear effect that deserves planned attention this year.',
  },
  4: {
    label: 'Major',
    guidance: 'Important effect that should be a management priority.',
  },
  5: {
    label: 'Critical',
    guidance: 'Very serious effect that needs urgent focus and ownership.',
  },
} as const

/**
 * Default plain-language questions replacing “financial / impact materiality” jargon.
 * Topic-specific overrides may be set on each MaterialityTopic.
 */
export const DEFAULT_BUSINESS_EFFECT_QUESTION =
  'How much could this affect your company’s money, customers, costs, financing, or ability to keep operating?'

export const DEFAULT_PEOPLE_PLANET_QUESTION =
  'How serious could the effect be on people or the environment because of your company’s activities?'

export function businessEffectQuestion(topicName: string, override?: string): string {
  if (override?.trim()) return override.trim()
  return `Thinking about “${topicName}”: ${DEFAULT_BUSINESS_EFFECT_QUESTION}`
}

export function peoplePlanetQuestion(topicName: string, override?: string): string {
  if (override?.trim()) return override.trim()
  return `Thinking about “${topicName}”: ${DEFAULT_PEOPLE_PLANET_QUESTION}`
}

export const READINESS_OPTIONS = [
  { value: 'implemented_documented', label: 'Implemented and documented', score: 1 },
  { value: 'implemented_informal', label: 'Implemented but informal', score: 0.75 },
  { value: 'partial', label: 'Partially implemented', score: 0.45 },
  { value: 'not_implemented', label: 'Not implemented', score: 0.1 },
  { value: 'not_sure', label: 'Not sure', score: null },
  { value: 'not_applicable', label: 'Not applicable', score: null },
] as const
